import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Chip,
  Grid,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanel
} from '@mui/joy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoIcon from '@mui/icons-material/Photo';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FileUploadService } from '../utils/fileUploadService';

const FileManager = ({ surveyProjectId, onFilesChange }) => {
  const [files, setFiles] = useState({ documents: [], photos: [], totalFiles: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const loadFiles = async () => {
    if (!surveyProjectId) return;

    setLoading(true);
    setError('');

    try {
      const result = await FileUploadService.getProjectFiles(surveyProjectId);
      
      if (result.success) {
        setFiles(result.data);
        if (onFilesChange) {
          onFilesChange(result.data);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(`Failed to load files: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [surveyProjectId]);

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    setDeleting(true);
    try {
      const result = await FileUploadService.deleteFile(
        fileToDelete.id,
        fileToDelete.fileType,
        fileToDelete.file_path
      );

      if (result.success) {
        await loadFiles(); // Refresh the list
        setDeleteModalOpen(false);
        setFileToDelete(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewModalOpen(true);
  };

  const handleDownload = (file) => {
    window.open(file.file_path, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const FileCard = ({ file }) => {
    const isPhoto = file.fileType === 'photos';
    
    return (
      <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* File Icon/Thumbnail */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 1,
            minHeight: '48px',
            alignItems: 'center'
          }}>
            {isPhoto ? (
              <Box 
                sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: 1, 
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'neutral.300',
                  cursor: 'pointer'
                }}
                onClick={() => handlePreview(file)}
              >
                <img
                  src={file.file_path}
                  alt={file.file_name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'none', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'neutral.100'
                }}>
                  <PhotoIcon sx={{ fontSize: 24, color: 'neutral.500' }} />
                </Box>
              </Box>
            ) : (
              <InsertDriveFileIcon sx={{ fontSize: 48, color: 'neutral.500' }} />
            )}
          </Box>

          {/* File Info */}
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <Typography 
              level="body2" 
              sx={{ 
                fontWeight: 'md', 
                mb: 0.5,
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
              title={file.file_name}
            >
              {file.file_name}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip 
                size="sm" 
                variant="soft" 
                color={isPhoto ? 'primary' : 'neutral'}
              >
                {isPhoto ? 'Photo' : 'Document'}
              </Chip>
            </Box>

            <Typography level="body3" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {file.formattedSize}
            </Typography>
            
            {isPhoto && file.image_width && file.image_height && (
              <Typography level="body3" sx={{ color: 'text.secondary', mb: 0.5 }}>
                {file.image_width} × {file.image_height}
              </Typography>
            )}

            <Typography level="body3" sx={{ color: 'text.tertiary' }}>
              {formatDate(file.created_at)}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            {isPhoto && (
              <IconButton
                size="sm"
                variant="outlined"
                onClick={() => handlePreview(file)}
                title="Preview"
              >
                <VisibilityIcon />
              </IconButton>
            )}
            <IconButton
              size="sm"
              variant="outlined"
              onClick={() => handleDownload(file)}
              title="Download"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="sm"
              color="danger"
              variant="outlined"
              onClick={() => handleDeleteClick(file)}
              title="Delete"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
    );
  };

  if (!surveyProjectId) {
    return (
      <Alert color="neutral">
        Select a survey project to view files
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
            folder_shared
          </span>
          Project Files ({files.totalFiles})
        </Typography>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<RefreshIcon />}
          onClick={loadFiles}
          loading={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && files.totalFiles === 0 ? (
        <Typography level="body2" sx={{ textAlign: 'center', py: 4 }}>
          Loading files...
        </Typography>
      ) : files.totalFiles === 0 ? (
        <Typography level="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          No files uploaded yet
        </Typography>
      ) : (
        <Tabs defaultValue={0}>
          <TabList>
            <Tab>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsertDriveFileIcon sx={{ fontSize: '16px' }} />
                Documents ({files.documents.length})
              </Box>
            </Tab>
            <Tab>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoIcon sx={{ fontSize: '16px' }} />
                Photos ({files.photos.length})
              </Box>
            </Tab>
          </TabList>

          <TabPanel value={0}>
            <Box sx={{ mb: 2 }}>
              <Typography level="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  info
                </span>
                Documents are stored in the survey-documents bucket
              </Typography>
            </Box>
            {files.documents.length === 0 ? (
              <Typography level="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No documents uploaded
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {files.documents.map((file) => (
                  <Grid xs={12} sm={6} md={4} key={file.id}>
                    <FileCard file={file} />
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={1}>
            <Box sx={{ mb: 2 }}>
              <Typography level="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  info
                </span>
                Photos are stored in the survey-photos bucket
              </Typography>
            </Box>
            {files.photos.length === 0 ? (
              <Typography level="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No photos uploaded
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {files.photos.map((file) => (
                  <Grid xs={12} sm={6} md={4} key={file.id}>
                    <FileCard file={file} />
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>
        </Tabs>
      )}

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => !deleting && setDeleteModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Delete File
          </Typography>
          <Typography level="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete "{fileToDelete?.file_name}"? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={confirmDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Preview Modal */}
      <Modal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: '90vw', maxHeight: '90vh', p: 2 }}>
          <ModalClose />
          {previewFile && (
            <>
              <Typography level="h4" sx={{ mb: 2 }}>
                {previewFile.file_name}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                maxHeight: 'calc(90vh - 120px)',
                overflow: 'hidden'
              }}>
                <img
                  src={previewFile.file_path}
                  alt={previewFile.file_name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default FileManager;